import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CircleAlert,
  CircleCheck,
  Copy,
  Info,
  LoaderCircle,
  LogOut,
  MessageCircle,
  Repeat,
  ShieldAlert,
  TriangleAlert,
  Wrench,
  X,
} from 'lucide-react';
import type { Announcement, PlatformStatus } from '@teaplus/shared';
import { api, API_EVENTS } from '@/lib/api';
import { qk, useSession } from '@/lib/queries';
import { Button, buttonVariants } from './ui/button';
import { TeaPlusLogo } from './brand';
import { useSignOut, useSwitchProfile } from './layout/app-header';

const KEY = 'teaplus-dismissed-announcements';
const STATUS_KEY = qk.platformStatus;
/** Where Tea+ Support answers: the Tea+ Discord server. */
export const SUPPORT_URL = 'https://discord.gg/Nf5HHZEMZt';

function dismissedIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/**
 * Maintenance mode, announcements and whether this account is restricted, as set by the owner.
 * Checked on start, every 30 seconds, and straight away when the live connection says so.
 */
export function usePlatformStatus() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => api<PlatformStatus>('/api/system/status'),
    refetchInterval: 30_000,
    retry: false,
  });
  // Someone else signed in, or another profile chosen: check for them straight away.
  const session = useSession().data;
  const who = session ? `${session.user.id}:${session.activeProfileId ?? ''}` : '';
  useEffect(() => {
    void qc.invalidateQueries({ queryKey: STATUS_KEY });
  }, [qc, who]);
  // Any request turned away for maintenance or a restricted account shows its screen straight away.
  useEffect(() => {
    const on = () => void qc.invalidateQueries({ queryKey: STATUS_KEY });
    window.addEventListener(API_EVENTS.maintenance, on);
    window.addEventListener(API_EVENTS.restricted, on);
    return () => {
      window.removeEventListener(API_EVENTS.maintenance, on);
      window.removeEventListener(API_EVENTS.restricted, on);
    };
  }, [qc]);
  return q.data;
}

const STYLE: Record<Announcement['level'], { icon: typeof Info; ring: string; tint: string }> = {
  info: { icon: Info, ring: 'border-pink/30', tint: 'text-pink' },
  success: { icon: CircleCheck, ring: 'border-success/35', tint: 'text-success' },
  warning: { icon: TriangleAlert, ring: 'border-warning/40', tint: 'text-warning' },
  critical: { icon: CircleAlert, ring: 'border-danger/45', tint: 'text-danger' },
};

export function Announcements() {
  const status = usePlatformStatus();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(dismissedIds);
  const shown = (status?.announcements ?? []).filter((a) => !dismissed.includes(a.id)).slice(0, 2);
  if (
    !shown.length ||
    location.pathname.startsWith('/watch') ||
    status?.maintenance.enabled ||
    status?.restricted
  )
    return null;
  const dismiss = (id: string) => {
    const next = [...dismissed, id].slice(-50);
    setDismissed(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {}
  };
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-14 z-[55] flex flex-col items-center gap-2 px-4"
      role="region"
      aria-label="Announcements"
    >
      {shown.map((a) => {
        const s = STYLE[a.level] ?? STYLE.info;
        return (
          <div
            key={a.id}
            role="status"
            className={`pointer-events-auto flex w-full max-w-2xl items-start gap-3 rounded-2xl border ${s.ring} bg-bg-2/95 px-4 py-3 shadow-[0_20px_60px_-24px_rgba(0,0,0,.9)] backdrop-blur-xl animate-[rise_380ms_var(--ease-out-soft)]`}
          >
            <s.icon className={`mt-0.5 size-5 shrink-0 ${s.tint}`} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-text">{a.title}</p>
              <p className="mt-0.5 text-sm text-muted">{a.body}</p>
            </div>
            {a.dismissible && (
              <button
                type="button"
                onClick={() => dismiss(a.id)}
                className="rounded-full p-1 text-muted hover:bg-elevated hover:text-text"
                aria-label="Dismiss announcement"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Covers the app while the owner has maintenance mode on. Goes away by itself when it's off. */
export function MaintenanceScreen() {
  const status = usePlatformStatus();
  const qc = useQueryClient();
  const [checking, setChecking] = useState(false);
  const m = status?.maintenance;
  const on = !!m?.enabled;
  const [wasOn, setWasOn] = useState(false);

  useEffect(() => {
    if (on) setWasOn(true);
    // Back from maintenance: everything that failed meanwhile loads again.
    if (!on && wasOn) {
      setWasOn(false);
      void qc.invalidateQueries();
    }
  }, [on, wasOn, qc]);

  useEffect(() => {
    if (!on) return;
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    return () => root?.removeAttribute('inert');
  }, [on]);

  if (!on || !m) return null;
  const until = m.until ? new Date(m.until) : null;
  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-title"
      className="fixed inset-0 z-[95] grid place-items-center bg-bg/95 px-6 backdrop-blur-xl"
    >
      <div className="w-full max-w-md text-center">
        <TeaPlusLogo className="mx-auto mb-8 h-8 w-auto" />
        <div className="mx-auto mb-5 grid size-14 place-items-center rounded-full bg-pink/10 text-pink">
          <Wrench className="size-6" aria-hidden />
        </div>
        <h1 id="maintenance-title" className="font-display text-3xl font-semibold text-text">
          Back very soon
        </h1>
        <p className="mt-3 text-base text-muted">{m.message}</p>
        {until && until.getTime() > Date.now() && (
          <p className="mt-2 text-sm text-muted">
            Expected back around{' '}
            {until.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {until.toDateString() !== new Date().toDateString()
              ? ` on ${until.toLocaleDateString()}`
              : ''}
            .
          </p>
        )}
        <Button
          className="mt-8"
          variant="secondary"
          disabled={checking}
          onClick={async () => {
            setChecking(true);
            await qc.refetchQueries({ queryKey: STATUS_KEY }).catch(() => undefined);
            setChecking(false);
          }}
        >
          {checking && <LoaderCircle className="size-4 animate-spin" />}
          Try again
        </Button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Covers the app while the owner has restricted this account, or just the profile in use: nothing
 * else works until it is lifted (checked every 30 seconds), someone signs out or, for a profile,
 * switches to another one. Shows the account or profile ID for support.
 */
export function RestrictedScreen() {
  const status = usePlatformStatus();
  const qc = useQueryClient();
  const signOut = useSignOut();
  const switchProfile = useSwitchProfile();
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState(false);
  const account = status?.restricted ?? null;
  const profile = account ? null : (status?.profileRestricted ?? null);
  const id = account?.id ?? profile?.id ?? null;
  const what = account ? 'Account' : 'Profile';
  const [wasOn, setWasOn] = useState(false);
  // Leaving (signing out or switching profile): nothing is restricted any more. Set before the
  // cache is cleared, so the screen sees it.
  const leave = () =>
    qc.setQueryData<PlatformStatus>(
      STATUS_KEY,
      (s) => s && { ...s, restricted: null, profileRestricted: null },
    );

  useEffect(() => {
    if (id) setWasOn(true);
    // Made active again: everything that failed meanwhile loads again.
    if (!id && wasOn) {
      setWasOn(false);
      void qc.invalidateQueries();
    }
  }, [id, wasOn, qc]);

  useEffect(() => {
    if (!id) return;
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    document.querySelectorAll('video').forEach((v) => v.pause());
    return () => root?.removeAttribute('inert');
  }, [id]);

  if (!id) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <BlockingScreen
      id="restricted"
      className="z-[96]"
      icon={<ShieldAlert className="size-6" aria-hidden />}
      iconClassName="bg-danger/10 text-danger"
      title={`${what} restricted`}
      body={
        profile ? (
          <>
            <span className="font-medium text-text">{profile.name}</span> has been restricted, so
            this profile can no longer use Tea+. Your other profiles still work. Please contact Tea+
            Support for more information and give them the profile ID.
          </>
        ) : (
          <>
            Your account has been restricted, so you can no longer use Tea+. Please contact Tea+
            Support for more information and give them your account ID.
          </>
        )
      }
    >
      <div className="mx-auto mt-6 flex max-w-sm items-center gap-2 rounded-xl border border-white/10 bg-bg-2 py-2 pl-4 pr-2 text-left">
        <span className="min-w-0 flex-1">
          <span className="block text-xs text-muted">{what} ID</span>
          <span className="block truncate font-mono text-sm text-text">{id}</span>
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={copy}
          aria-label={copied ? `${what} ID copied` : `Copy ${what.toLowerCase()} ID`}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <a
          href={SUPPORT_URL}
          target="_blank"
          rel="noreferrer"
          className={buttonVariants({ variant: 'primary' })}
        >
          <MessageCircle className="size-4" />
          Contact Tea+ Support
        </a>
        {profile && (
          <Button
            variant="secondary"
            onClick={async () => {
              leave();
              await switchProfile().catch(() => undefined);
              // A request still on its way may have shown the screen again: check now.
              await qc.refetchQueries({ queryKey: STATUS_KEY }).catch(() => undefined);
            }}
          >
            <Repeat className="size-4" />
            Switch profile
          </Button>
        )}
        <Button
          variant="secondary"
          disabled={checking}
          onClick={async () => {
            setChecking(true);
            await qc.refetchQueries({ queryKey: STATUS_KEY }).catch(() => undefined);
            setChecking(false);
          }}
        >
          {checking && <LoaderCircle className="size-4 animate-spin" />}
          Check again
        </Button>
      </div>
      <button
        type="button"
        onClick={async () => {
          leave();
          await signOut();
        }}
        className="mx-auto mt-6 flex items-center gap-1.5 text-sm text-muted hover:text-text"
      >
        <LogOut className="size-4" />
        Sign out
      </button>
    </BlockingScreen>
  );
}

/** The full screen that stops the app until something is sorted out, with the Tea+ logo on top. */
export function BlockingScreen({
  id,
  className,
  icon,
  iconClassName,
  title,
  body,
  children,
}: {
  id: string;
  /** Its stacking order, as a z- class. */
  className: string;
  icon: ReactNode;
  iconClassName: string;
  title: string;
  body: ReactNode;
  children: ReactNode;
}) {
  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-body`}
      className={`fixed inset-0 grid place-items-center overflow-y-auto bg-bg/80 px-6 py-10 backdrop-blur-2xl ${className}`}
    >
      <div className="w-full max-w-md text-center">
        <TeaPlusLogo className="mx-auto mb-8 h-8 w-auto" />
        <div
          className={`mx-auto mb-5 grid size-14 place-items-center rounded-full ${iconClassName}`}
        >
          {icon}
        </div>
        <h1 id={`${id}-title`} className="font-display text-3xl font-semibold text-text">
          {title}
        </h1>
        <p id={`${id}-body`} className="mt-3 text-base text-muted">
          {body}
        </p>
        {children}
      </div>
    </div>,
    document.body,
  );
}
